/** The next session's behavior half: the dashboard's next booking in the home's words. Draws nothing. */
import { useMemo } from 'react';
import { virtualPlatformLabel } from '@telify/shared/browser';
import { formatCardTime, formatHeroDate, getBookingCardPill } from '@/pages/BookingHubPage/homeFormat';
import type { UpcomingBooking } from '@/types/dashboard';
import { buildMapsUrl } from '@/utils/mapLinks';

import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { keepTimes } from '../../format';
import { useWebDashboard } from '../../hooks/useWebAccount';
import { usePageMover } from '../../pageMover';
import { bookingPhoto } from '../../ui/Photo';
import { contract, type NextSessionActions, type NextSessionBooking, type NextSessionEvents, type NextSessionState } from './contract';

export type NextSessionRuntime = PieceRuntime<typeof contract, NextSessionState, NextSessionActions>;

function whereLine(b: UpcomingBooking): string {
  const who = b.provider?.name ? `With ${b.provider.name}` : '';
  const loc = b.location;
  // service-by-phone D14: the studio calls; never the number on the home.
  if (loc.isByPhone) return who ? `${who}, by phone` : 'By phone';
  if (loc.isVirtual) {
    const platform = virtualPlatformLabel(loc.virtualPlatform);
    const online = platform ? `online on ${platform}` : 'online';
    return who ? `${who}, ${online}` : `O${online.slice(1)}`;
  }
  const place = [loc.name, loc.address].filter(Boolean).join(', ');
  if (!place) return who;
  return who ? `${who} at ${place}` : place;
}

function nextSessionOf(b: UpcomingBooking): NextSessionBooking {
  const online = b.location.isVirtual;
  return {
    id: b.id,
    name: b.service.name,
    photoUrl: b.service.imageUrl,
    photo: bookingPhoto(b.service),
    status: getBookingCardPill(b).label,
    when: keepTimes(`${formatHeroDate(b.scheduledAt)} at ${formatCardTime(b.scheduledAt)}`),
    length: b.service.kind === 'event' ? '' : `, ${b.duration} minutes`,
    where: whereLine(b),
    href: `/account/bookings/${encodeURIComponent(b.id)}`,
    // The meeting link is only sent for a confirmed booking.
    joinHref: online && b.status === 'CONFIRMED' ? b.location.virtualLink : null,
    directionsHref: !online && !b.location.isByPhone && b.status !== 'PENDING_APPROVAL' ? buildMapsUrl(b.location) : null,
  };
}

export function useNextSession(input: PieceInput<NextSessionEvents> = {}): NextSessionRuntime {
  const options = usePieceOptions(contract, input.options);
  const navigate = usePageMover();
  const next = useWebDashboard().data?.nextBooking ?? null;
  const booking = useMemo(() => (next ? nextSessionOf(next) : null), [next]);
  const actions = useMemo<NextSessionActions>(() => ({ open: (href) => navigate(href) }), [navigate]);
  return { state: { booking }, actions, options };
}
